import axios from "axios";
import translateService from "./translateService";
import GraphqlClientService from "./GraphqlClientService";

// Document links (backend docs/sharing-runbook.md §4-§5, public-document-links.md §3):
// an e-mailed link's secret (`vs-…`, route `l#vs-…`) or a printed QR code's
// public link (`vw-…`, route `auth/api/public/authWithHash/pdf/vw-…`) is
// exchanged (RFC 8693) for a 15-minute token that reaches that one document.
//
// The token lives in a per-tab slot in sessionStorage, never localStorage.
// While a tab holds a slot, its token wins over a Keycloak session (that tab
// never sends the Keycloak token), it is renewed by exchanging the same value
// again, and leaving the document clears it.

const SLOT_KEY = "validatege.link-session";
const REFUSAL_KEY = "validatege.link-refusal";

const SUBJECT_TOKEN_TYPE = "urn:aisitec:params:oauth:token-type:document-link";
const TOKEN_EXCHANGE = "urn:ietf:params:oauth:grant-type:token-exchange";
const RENEW_BEFORE_MS = 2 * 60 * 1000;

export const LINK_KIND = { share: "share", publicView: "public" };

// the backend's problem types are https://www.aisitec.com/<TYPE>
const problemType = (error) => {
  const body = error && error.response && error.response.data;
  const type = body && typeof body === "object" && body.type;
  return type ? String(type).split("/").pop() : null;
};

// own client: no global interceptors (their `locale` header forces a CORS
// preflight, their error modal would open on the 400 refusals)
const exchangeClient = axios.create();

class LinkSessionService {
  constructor() {
    this._renewing = null;
  }

  // ---- slot ----------------------------------------------------------

  get() {
    try {
      const raw = sessionStorage.getItem(SLOT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  has() {
    return !!this.get();
  }

  isShareLink() {
    const slot = this.get();
    return !!slot && slot.kind === LINK_KIND.share;
  }

  isPublicView() {
    const slot = this.get();
    return !!slot && slot.kind === LINK_KIND.publicView;
  }

  _store(slot) {
    sessionStorage.setItem(SLOT_KEY, JSON.stringify(slot));
  }

  clear() {
    sessionStorage.removeItem(SLOT_KEY);
  }

  // ---- exchange --------------------------------------------------------

  // POST {api}oauth2/token; resolves with the answer, rejects with
  // {status, error, reason, description, extras} for a refusal
  exchange(value) {
    const form = new URLSearchParams();
    form.set("grant_type", TOKEN_EXCHANGE);
    form.set("client_id", globalConfig.oidc_client_id);
    form.set("subject_token", value);
    form.set("subject_token_type", SUBJECT_TOKEN_TYPE);
    return exchangeClient
      .post(`${globalConfig.api_URL}oauth2/token`, form.toString(), {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Accept-Language": translateService.key || "ka",
        },
      })
      .then((res) => res.data)
      .catch((err) => {
        const body = (err && err.response && err.response.data) || {};
        const { error, error_description, reason, ...extras } =
          typeof body === "object" ? body : {};
        return Promise.reject({
          status: err && err.response ? err.response.status : 0,
          error: error || null,
          reason: reason || null,
          description: error_description || null,
          extras,
        });
      });
  }

  // a new session in this tab: exchange, fill the slot; a Keycloak session in
  // this tab is not touched, only outranked
  open(value, kind) {
    return this.exchange(value).then((answer) => {
      this._store(this._slotFrom(value, kind, answer));
      sessionStorage.removeItem(REFUSAL_KEY);
      // link sessions never use the notification subscription
      GraphqlClientService.CloseConnection();
      return this.get();
    });
  }

  _slotFrom(value, kind, answer) {
    return {
      value,
      kind,
      accessToken: answer.access_token,
      expiresAt: Date.now() + (answer.expires_in || 900) * 1000,
      // the exposed ids: every call names the document and the workspace by
      // them (the numeric doc_id and workspace_id are not used)
      docId: answer.doc_exposed_id,
      workspaceId: answer.workspace_exposed_id,
      linkExpiresAt: answer.link_expires_at || null,
    };
  }

  // the slot's token, renewed first when it has less than two minutes left
  // (or `minMs`); null when the renewal was refused (the reason page is shown)
  token(minMs = RENEW_BEFORE_MS) {
    const slot = this.get();
    if (!slot) return Promise.resolve(null);
    if (slot.expiresAt - Date.now() > minMs) return Promise.resolve(slot.accessToken);
    return this.renew().then((s) => (s ? s.accessToken : null));
  }

  // exchange the same value again, one exchange at a time per tab
  renew() {
    if (this._renewing) return this._renewing;
    const slot = this.get();
    if (!slot) return Promise.resolve(null);
    this._renewing = this.exchange(slot.value)
      .then((answer) => {
        const current = this.get();
        // the tab left the document meanwhile
        if (!current || current.value !== slot.value) return null;
        this._store(this._slotFrom(slot.value, slot.kind, answer));
        return this.get();
      })
      .catch((refusal) => {
        this.end(refusal.reason || "LINK_UNKNOWN", refusal.extras, refusal.description, slot.kind);
        return null;
      })
      .finally(() => {
        this._renewing = null;
      });
    return this._renewing;
  }

  // ---- ending ------------------------------------------------------------

  // the session is over: clear the slot and show the reason page
  end(reason, extras, description, kind) {
    const slot = this.get();
    this.clear();
    sessionStorage.setItem(
      REFUSAL_KEY,
      JSON.stringify({
        reason,
        extras: extras || {},
        description: description || null,
        kind: kind || (slot && slot.kind) || LINK_KIND.share,
      })
    );
    window.location.assign(`${globalConfig.base_URL}l`);
  }

  // the refusal the reason page shows, read once
  takeRefusal() {
    try {
      const raw = sessionStorage.getItem(REFUSAL_KEY);
      sessionStorage.removeItem(REFUSAL_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  // the reason a backend answer ends this tab's link session, or null:
  // 403 SHARE_WITHDRAWN, 410 DOCUMENT_DELETED, and a public view's 403
  // LINK_SCOPE (it calls only what it may, so out of scope means the printed
  // link stopped working: expired, or its document's row hidden). A share
  // link's LINK_SCOPE is a website bug, not an end. No side effects.
  sessionRefusal(error) {
    if (!this.has()) return null;
    const status = error && error.response && error.response.status;
    const type = problemType(error);
    if (status === 403 && type === "SHARE_WITHDRAWN") return "SHARE_WITHDRAWN";
    if (status === 410 && type === "DOCUMENT_DELETED") return "DOCUMENT_DELETED";
    if (status === 403 && type === "LINK_SCOPE" && this.isPublicView()) return "LINK_EXPIRED";
    return null;
  }

  // task 3: such an answer ends the session with its page (never a
  // re-exchange); true when it did
  handleRefusal(error) {
    const reason = this.sessionRefusal(error);
    if (!reason) return false;
    this.end(reason);
    return true;
  }

  // ---- what a session may call (share-prompt task 7) ---------------------

  // Whether this call is one the tab's link may make: every other call
  // answers 403 LINK_SCOPE, so it is never sent (a component shared with the
  // signed-in pages may still try one). Public and token-exchange paths need
  // no token. A path's document id must be the token's doc_id.
  inScope(config) {
    const slot = this.get();
    if (!slot) return true;
    const base = globalConfig.api_URL;
    const url = String(config.url || "");
    if (!url.startsWith(base)) return true;
    const path = url.slice(base.length).split("?")[0];
    if (path.startsWith("api/public/") || path.startsWith("oauth2/")) return true;
    const method = String(config.method || "get").toLowerCase();
    const doc = `api/v2/private/document/${slot.docId}`;
    const read = method === "get" || method === "head";
    if (read && (path === "api/private/userInfo" || path === "api/v2/private/userInfo")) return true;
    if (read && (path === doc || path === `${doc}/link`)) return true;
    // a public view reads, and nothing else
    if (slot.kind !== LINK_KIND.share) return false;
    if (method === "get" && path === `${doc}/signing-options`) return true;
    // who signed through this link's share (actionRights.signingList)
    if (method === "get" && path === `${doc}/signings`) return true;
    if (method === "post" && new RegExp(`^${doc}/shares/\\d+/decline$`).test(path)) return true;
    // task 22: a free validation starts when its document is opened
    if (method === "post" && path === `api/v2/private/free-validations/${slot.docId}/start`) return true;
    // eID's facsimile, made for this link alone (owner, 2026-10-11); a free
    // validation's link signs nothing and is refused (SIGN_NOT_GRANTED)
    if (method === "post" && path === "api/private/uploadfacsimile") return true;
    return false;
  }

  // ---- leaving the document --------------------------------------------

  // A page load elsewhere with a slot still in this tab's sessionStorage (a
  // duplicated tab, a reload after leaving through the address bar): the
  // slot is not this page's. Called before the session starts, so the page
  // runs on the tab's Keycloak session, if any.
  // A slot kept before 2026-10-13 holds the numeric document id, which no
  // call accepts any more: dropped too.
  dropIfElsewhere(pathname) {
    const slot = this.get();
    if (!slot) return;
    const exposed = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(slot.docId));
    if (!exposed || !this._keepsSlot(pathname)) this.clear();
  }

  // while a slot is held, only the document's own pages keep it
  _keepsSlot(pathname) {
    const slot = this.get();
    if (!slot) return true;
    const base = globalConfig.base_URL;
    const path = pathname.startsWith(base) ? pathname.slice(base.length) : pathname;
    return (
      path === `viewResult/${slot.docId}` ||
      (slot.kind === LINK_KIND.share && path.startsWith("sign/")) ||
      path === "l" ||
      path.startsWith("auth/api/public/authWithHash/pdf/")
    );
  }

  // leaving it clears the slot and starts the page afresh, so the tab uses
  // its Keycloak session, if any
  watch(history) {
    if (this._watching === history) return;
    this._watching = history;
    history.listen((location) => {
      if (this.has() && !this._keepsSlot(location.pathname)) {
        this.clear();
        window.location.assign(location.pathname + location.search);
      }
    });
  }

  leave(path) {
    this.clear();
    window.location.assign(`${globalConfig.base_URL}${path || ""}`);
  }
}

export default new LinkSessionService();
